import style from "./style.scss";
import { Fragment, ComponentChildren, RefObject, JSX } from "preact";
import {
  useState,
  useEffect,
  useRef,
  useMemo,
  useId,
  useCallback,
} from "preact/hooks";
import { formatDateLong } from "../../components/helpers";
import { usePopup } from "../popup/context";
import Popup from "../popup";
import NoWrap from "../no-wrap";
import Editorial from "../editorial";
import Link, {
  getLinkProps,
  LinkProps,
  Button,
  ButtonList,
} from "../../components/link";
import Warning from "../../components/warning";
import { formatRegions } from "./formatRegions";

/**
 * Defines the navigation behavior when a user copies / interacts with a coupon:
 *
 * - `"tab-swap"` (Default):
 *    - Copies code to clipboard
 *    - Opens new tab with rough clone of the current page (passes query string params to open a discount modal and then remove query string params)
 *    - Redirects active window to the affiliate merchant target (optional shopify redirect to apply discount code)
 *
 * - `"direct"`:
 *    - Copies code to clipboard
 *    - Redirects active window directly to the affiliate merchant (optional shopify redirect to apply discount code)
 *
 * - `"copy-only"`:
 *    - Copies code to clipboard
 *    - Triggers UI "Copied!" feedback label without performing any navigation or opening tabs
 */
export type OutclickMode = "tab-swap" | "direct" | "copy-only";

const DefaultOutclickMode: OutclickMode =
  process.env.DISCOUNT_OUTCLICK === "direct" ||
  process.env.DISCOUNT_OUTCLICK === "copy-only"
    ? process.env.DISCOUNT_OUTCLICK
    : "tab-swap";

const ShopifyRedirectsEnabled =
  process.env.DISCOUNT_SHOPIFY_redirect !== "false";

const PARAMS = {
  OUTCLICKED: "_occ",
  TARGET: "_oct",
  OFFSET: "_ocy",
};

export interface LinkData extends LinkProps {
  icon?: string;
  shopify?: boolean;
}

// Safely dispatches Google Analytics events
const trackCouponClick = (
  code: string,
  href: string,
  locationLabel: string,
  brand: string
) => {
  if (typeof gtag !== "undefined") {
    gtag("event", "select_discount", {
      brand: brand,
      discount_code: code,
      location_id: locationLabel,
      destination_url: href,
      transport: "beacon",
    });
  }
};

// Formats merchant URL for Shopify discount redirect
const getFormattedLinkData = (linkData: LinkData, code: string): LinkData => {
  if (!ShopifyRedirectsEnabled || !linkData.shopify) return linkData;

  try {
    const rawUrl = new URL(linkData.href);
    const domain = rawUrl.origin;
    const pathAndQuery = rawUrl.pathname + rawUrl.search + rawUrl.hash;

    const redirectParam =
      pathAndQuery && pathAndQuery !== "/"
        ? `?redirect=${encodeURIComponent(pathAndQuery)}`
        : "";

    return {
      ...linkData,
      href: `${domain}/discount/${encodeURIComponent(code)}${redirectParam}`,
    };
  } catch {
    return linkData;
  }
};

// Handles outclick redirection & duplicate tab opening based on mode
const executeOutclick = (
  code: string,
  href: string,
  targetEl: HTMLElement | null | undefined,
  outclickMode: OutclickMode
) => {
  if (outclickMode === "copy-only") return;
  if (outclickMode === "direct") {
    window.location.href = href;
    return;
  }

  const siteUrl = new URL(window.location.href);
  siteUrl.searchParams.set(PARAMS.OUTCLICKED, code);

  if (targetEl?.getBoundingClientRect) {
    if (targetEl.id) {
      siteUrl.searchParams.set(PARAMS.TARGET, targetEl.id);
    }
    const viewportOffset = Math.round(targetEl.getBoundingClientRect().top);
    siteUrl.searchParams.set(PARAMS.OFFSET, viewportOffset.toString());
  }

  window.open(siteUrl.toString(), "_blank", "noopener,noreferrer"); //Important to keep "noopener,noreferrer" for security
  window.location.href = href;
};

// Check if code matches active outclick state
const checkInitialOutclicked = (code: string) => {
  if (typeof window === "undefined" || !code) return false;
  if (window.__outclickedCode === code) return true;

  const params = new URLSearchParams(window.location.search);
  const matched = params.get(PARAMS.OUTCLICKED) === code;

  if (matched) {
    window.__outclickedCode = code;
  }
  return matched;
};

// Encapsulates clipboard copying, state tracking, and outclick triggers
const useCouponCopy = (
  code: string,
  linkData: LinkData,
  targetRef: RefObject<HTMLDivElement> | undefined,
  clickLocation: string,
  outclickMode: OutclickMode,
  brand: string
) => {
  const [alreadyOutclicked, setAlreadyOutclicked] = useState(() =>
    checkInitialOutclicked(code)
  );
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(
    (e?: JSX.TargetedMouseEvent<HTMLAnchorElement>) => {
      if (e) {
        e.preventDefault();
      }

      // Compute processed link properties (affiliate URL resolving, target, rel, etc.)
      const resolvedProps = getLinkProps(linkData);

      trackCouponClick(code, resolvedProps.href, clickLocation, brand);

      // Clipboard Copy
      if (navigator.clipboard && code) {
        navigator.clipboard
          .writeText(code)
          .then(() => {
            // Success: Show feedback
            setIsCopied(true);

            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setIsCopied(false), 3000);
          })
          .catch((err) => {
            // Failure: Silently swallow error or log it (isCopied stays false)
            console.warn("Clipboard copy failed:", err);
          });
      }

      // Outclick Launch
      if (outclickMode === "copy-only") return;

      if (!alreadyOutclicked && typeof window !== "undefined") {
        setAlreadyOutclicked(true);
        executeOutclick(
          code,
          resolvedProps.href,
          targetRef?.current,
          outclickMode
        );
      } else if (alreadyOutclicked && outclickMode === "direct") {
        window.location.href = resolvedProps.href;
      }
    },
    [
      code,
      linkData,
      targetRef,
      clickLocation,
      outclickMode,
      brand,
      alreadyOutclicked,
    ]
  );

  return {
    alreadyOutclicked,
    isCopied,
    hasBeenCopied: isCopied, // Fix: won't trigger automatically on page reload / outclick clone tab
    handleCopy,
  };
};

// Restores viewport-relative scroll position on outclick return
const useScrollRestoration = (
  code: string,
  containerRef: RefObject<HTMLDivElement>,
  openDetailsPopup: () => void
) => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has(PARAMS.OUTCLICKED)) return;

    const outclickedCode = url.searchParams.get(PARAMS.OUTCLICKED);
    if (outclickedCode !== code) return;

    const targetId = url.searchParams.get(PARAMS.TARGET);
    const rawOffset = url.searchParams.get(PARAMS.OFFSET);

    window.__outclickedCode = code;
    openDetailsPopup();

    requestAnimationFrame(() => {
      const targetEl = targetId
        ? document.getElementById(targetId)
        : containerRef.current;

      if (targetEl?.getBoundingClientRect) {
        const viewportOffset = rawOffset ? parseInt(rawOffset, 10) : 0;
        const elementBaseY =
          targetEl.getBoundingClientRect().top + window.scrollY;
        const targetTop = Math.max(0, elementBaseY - viewportOffset);

        try {
          window.scrollTo({ top: targetTop, behavior: "auto" });
        } catch {
          window.scrollTo(0, targetTop);
        }
      }
    });

    // Cleanup parameters
    url.searchParams.delete(PARAMS.OUTCLICKED);
    url.searchParams.delete(PARAMS.TARGET);
    url.searchParams.delete(PARAMS.OFFSET);
    window.history.replaceState({}, "", url.toString());
  }, [code, openDetailsPopup, containerRef]);
};

const CopyCouponBox = ({
  code,
  linkData,
  targetRef,
  showCopiedText = false,
  clickLocation,
  outclickMode,
  brand,
}: {
  code: string;
  linkData: LinkData;
  targetRef: { current: HTMLDivElement | null };
  showCopiedText?: boolean;
  clickLocation: string;
  outclickMode: OutclickMode;
  brand: string;
}) => {
  const { hasBeenCopied, handleCopy } = useCouponCopy(
    code,
    linkData,
    targetRef,
    clickLocation,
    outclickMode,
    brand
  );

  const { children, ...iconLinkProps } = linkData;

  return (
    <span>
      <span class={style.codeBoxMini}>
        <strong
          onClick={handleCopy}
          class={style.codeText}
          draggable={false}
          style={{
            userSelect: "all",
            WebkitUserSelect: "all",
            cursor: "pointer",
          }}
        >
          {code}
        </strong>

        <Link
          {...iconLinkProps}
          onClick={handleCopy}
          class={style.copyIconLink}
          aria-label={`Copy code ${code} and open merchant store`}
        />
      </span>

      {showCopiedText && hasBeenCopied && (
        <span class={style.copiedLabel}>Copied!</span>
      )}
    </span>
  );
};

const MiniLayout = ({
  code,
  deal,
  dealShort,
  linkData,
  brand,
  containerRef,
  onOpenDetails,
  onMerchantClick,
  outclickMode,
}: {
  code: string;
  deal: string;
  dealShort: ComponentChildren;
  linkData: LinkData;
  brand: string;
  containerRef: RefObject<HTMLDivElement>;
  onOpenDetails: (e: JSX.TargetedMouseEvent<HTMLAnchorElement>) => void;
  onMerchantClick: (
    locationLabel: string
  ) => (e: JSX.TargetedMouseEvent<HTMLElement>) => void;
  outclickMode: OutclickMode;
}) => (
  <Fragment>
    <div class={style.mobile}>
      <div class={style.deal}>{deal}</div>
      <div>
        <CopyCouponBox
          code={code}
          linkData={linkData}
          targetRef={containerRef}
          clickLocation="code_box_mini_layout"
          outclickMode={outclickMode}
          brand={brand}
        />
        <Link href="#" onClick={onOpenDetails} class={style.detailsLink}>
          See details
        </Link>
      </div>
    </div>
    <div class={style.normal}>
      <div>
        <NoWrap>
          Use code{" "}
          <CopyCouponBox
            code={code}
            linkData={linkData}
            targetRef={containerRef}
            clickLocation="code_box_mini_layout"
            outclickMode={outclickMode}
            brand={brand}
          />
        </NoWrap>{" "}
        <NoWrap>
          at{" "}
          <Link
            {...linkData}
            onClick={onMerchantClick("mini_layout_inline_store_link")}
          />{" "}
          for {dealShort || deal}!
        </NoWrap>
      </div>
      <div class={style.details}>
        <Link href="#" onClick={onOpenDetails}>
          See details
        </Link>
      </div>
    </div>
  </Fragment>
);

const FullLayout = ({
  code,
  deal,
  linkData,
  brand,
  showRegions,
  regions,
  containerRef,
  onOpenDetails,
  onMerchantClick,
  outclickMode,
}: {
  code: string;
  deal: string;
  linkData: LinkData;
  brand: string;
  showRegions: boolean;
  regions: string[];
  containerRef: RefObject<HTMLDivElement>;
  onOpenDetails: (e: JSX.TargetedMouseEvent<HTMLAnchorElement>) => void;
  onMerchantClick: (
    locationLabel: string
  ) => (e: JSX.TargetedMouseEvent<HTMLElement>) => void;
  outclickMode: OutclickMode;
}) => (
  <Fragment>
    <h2 class={style.heading}>{deal}</h2>

    <div style="margin:10px 0;">
      <CopyCouponBox
        code={code}
        linkData={linkData}
        targetRef={containerRef}
        showCopiedText
        clickLocation="code_box_full_layout"
        outclickMode={outclickMode}
        brand={brand}
      />
    </div>

    <p class="caption-left">
      Exclusive discount for my readers on the{" "}
      <Link
        {...linkData}
        onClick={onMerchantClick("full_layout_inline_store_link")}
      />{" "}
      store. {showRegions && formatRegions(regions)} Exclusions apply, see{" "}
      <Link href="#" onClick={onOpenDetails}>
        details
      </Link>
      .
    </p>
  </Fragment>
);

const CouponCode = ({
  full = false,
  code,
  deal,
  dealShort,
  linkData,
  buttonText,
  terms,
  brand,
  regions,
  showRegions = false,
  expiration,
  outclickMode = DefaultOutclickMode,
}: {
  full?: boolean;
  code: string;
  deal: string;
  dealShort: JSX.Element;
  linkData: LinkData;
  buttonText: string;
  terms: string;
  brand: string;
  regions: string[];
  showRegions?: boolean;
  expiration: string;
  outclickMode?: OutclickMode;
}) => {
  const popup = usePopup();
  const modalTitleId = useId();
  const componentId = `coupon-${useId().replace(/:/g, "")}`;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const formattedLinkData = useMemo(
    () => getFormattedLinkData(linkData, code),
    [linkData, code]
  );

  const handleMerchantClick = useCallback(
    (locationLabel: string) => () => {
      const resolved = getLinkProps(formattedLinkData);
      trackCouponClick(code, resolved.href, locationLabel, brand);
    },
    [code, formattedLinkData, brand]
  );

  const openDetailsPopup = useCallback(
    (e?: JSX.TargetedMouseEvent<HTMLAnchorElement>) => {
      if (e) e.preventDefault();

      popup.showPopup(
        <Popup ariaLabelledBy={modalTitleId}>
          <div class={style.popupWrapper}>
            <Editorial>
              <h2 id={modalTitleId}>{deal}</h2>

              <p>
                Use code{" "}
                <CopyCouponBox
                  code={code}
                  linkData={formattedLinkData}
                  targetRef={containerRef}
                  showCopiedText
                  clickLocation="code_box_popup"
                  outclickMode={outclickMode}
                  brand={brand}
                />
              </p>

              <p class="caption-left">
                {terms} {formatRegions(regions)} Expires{" "}
                {formatDateLong(expiration)}.
              </p>
              <ButtonList>
                <Button
                  {...formattedLinkData}
                  onClick={handleMerchantClick("popup_button")}
                >
                  {buttonText}
                </Button>
              </ButtonList>
            </Editorial>
          </div>
        </Popup>
      );
    },
    [
      popup,
      modalTitleId,
      deal,
      code,
      formattedLinkData,
      terms,
      regions,
      expiration,
      buttonText,
      handleMerchantClick,
      outclickMode,
      brand,
    ]
  );

  useScrollRestoration(code, containerRef, openDetailsPopup);

  return (
    <Warning icon={false} green class={style.wrapperFrame}>
      <div
        id={componentId}
        ref={containerRef}
        class={full ? style.fullLayout : style.miniLayout}
      >
        {full ? (
          <FullLayout
            code={code}
            deal={deal}
            linkData={formattedLinkData}
            brand={brand}
            showRegions={showRegions}
            regions={regions}
            containerRef={containerRef}
            onOpenDetails={openDetailsPopup}
            onMerchantClick={handleMerchantClick}
            outclickMode={outclickMode}
          />
        ) : (
          <MiniLayout
            code={code}
            deal={deal}
            dealShort={dealShort}
            linkData={formattedLinkData}
            brand={brand}
            containerRef={containerRef}
            onOpenDetails={openDetailsPopup}
            onMerchantClick={handleMerchantClick}
            outclickMode={outclickMode}
          />
        )}
      </div>
    </Warning>
  );
};

export default CouponCode;
